/**
 * R2 consent: API stores massage/symptom selections as comma-separated strings (or JSON arrays).
 */
import {
  CONSENT_FORM_COMMON_LABEL_KEYS,
  R2_MASSAGE_PART_OPTIONS,
  R2_ORTHOMORPHIA_PART_OPTIONS,
} from '@/constants/consentForm';

export type ConsentServiceType = 'r2' | 'a2';

export function parseR2CheckboxList(val: unknown): string[] {
  if (Array.isArray(val)) return val.map(String).filter(Boolean);
  if (val == null || val === '') return [];
  if (typeof val === 'string') {
    const s = val.trim();
    if (!s) return [];
    if (s.startsWith('[')) {
      try {
        const j = JSON.parse(s) as unknown;
        if (Array.isArray(j)) return j.map(String).filter(Boolean);
      } catch {
        /* not JSON */
      }
    }
    return s
      .split(/[,、]/)
      .map(x => x.trim())
      .filter(Boolean);
  }
  return [];
}

export function joinR2Csv(parts: string[] | undefined): string {
  return (parts ?? []).filter(Boolean).join(',');
}

/**
 * `r_orthomorphias.id`（1 始まり）→ ラベル。`R2_ORTHOMORPHIA_PART_OPTIONS` の並びと DB seeder 一致。
 */
function r2OrthomorphiaIdToPartLabelMap(): Record<string, string> {
  return Object.fromEntries(R2_ORTHOMORPHIA_PART_OPTIONS.map((o, i) => [String(i + 1), o.value]));
}

/**
 * `r_massages.id`（1 始まり）→ ラベル。seeder は ortho 4 部位 + 躯幹（`R2_MASSAGE_PART_OPTIONS` 内）の順。
 */
function r2MassageIdToPartLabelMap(): Record<string, string> {
  const kukan = R2_MASSAGE_PART_OPTIONS.find(o => o.value === '躯幹')?.value;
  if (!kukan) {
    throw new Error('R2_MASSAGE_PART_OPTIONS must include 躯幹 for DB massage id mapping');
  }
  const ordered = [...R2_ORTHOMORPHIA_PART_OPTIONS.map(o => o.value), kukan];
  if (ordered.length !== 5) {
    throw new Error('R2 orthomorphia + 躯幹 must yield 5 r_massages ids');
  }
  return Object.fromEntries(ordered.map((label, i) => [String(i + 1), label]));
}

/**
 * Long-term frequent / month-data: `r_consents.massage`・`orthomorphia` は
 * ① マスタ id の CSV（例 "1,2"）② 部位名の CSV のいずれかで保存されることがある。
 * UI のチェックは部位名で行うため、正規化したラベル集合を返す。
 */
export function resolveR2TreatmentPartLabelsFromStoredConsent(
  massageRaw: string | null | undefined,
  orthomorphiaRaw: string | null | undefined
): Set<string> {
  const knownPartLabels = new Set<string>(R2_MASSAGE_PART_OPTIONS.map(o => o.value));
  const massageIdToLabel = r2MassageIdToPartLabelMap();
  const orthoIdToLabel = r2OrthomorphiaIdToPartLabelMap();

  const out = new Set<string>();
  const ingest = (raw: unknown, idMap: Record<string, string>) => {
    for (const token of parseR2CheckboxList(raw)) {
      if (knownPartLabels.has(token)) {
        out.add(token);
        continue;
      }
      if (/^\d+$/.test(token)) {
        const label = idMap[token];
        if (label) out.add(label);
      }
    }
  };
  ingest(massageRaw, massageIdToLabel);
  ingest(orthomorphiaRaw, orthoIdToLabel);
  return out;
}

export function getConsentFormRelevantLabelKeys(serviceType: ConsentServiceType): string[] {
  const keys: string[] = [...CONSENT_FORM_COMMON_LABEL_KEYS];
  if (serviceType === 'r2') keys.push('symptom', 'treatment_parts');
  return keys;
}
